import { retirementPlan } from "../../engine/calculations";

/**
 * Comprehensive retirement planning (Mission Financial Azadi model) — supersedes the earlier
 * need-based version by also accounting for retirement savings you've already made (grown at their
 * own return) before sizing the monthly SIP for whatever shortfall remains.
 */
const retirementPlanningConfig = {
  key: "retirement-planning",
  title: "Retirement Planning",
  category: "Retirement & Income",
  description: "Size the retirement corpus you need and the monthly SIP to build it, net of what you've already saved.",
  fields: [
    { key: "currentAge", label: "Current age", type: "number", default: 35, min: 18, max: 70 },
    { key: "retirementAge", label: "Retirement age", type: "number", default: 60, min: 40, max: 75 },
    { key: "lifeExpectancy", label: "Life expectancy (age)", type: "number", default: 85, min: 60, max: 100 },
    { key: "monthlyExpense", label: "Monthly expenses if retired today", type: "currency", default: 60_000, min: 1000, max: 50_00_000 },
    { key: "currentSavings", label: "Current savings for retirement", type: "currency", default: 10_00_000, min: 0, max: 100_00_00_000, help: "What you've already invested towards retirement." },
    { key: "inflation", label: "Inflation", type: "percent", default: 6, min: 0, max: 15, assumptionKey: "inflation" },
    { key: "postReturn", label: "Post-retirement return", type: "percent", default: 7, min: 1, max: 20, assumptionKey: "debtReturn", help: "Return on the corpus after you retire (usually more conservative)." },
    { key: "returnOnExisting", label: "Return on existing investments", type: "percent", default: 10, min: 1, max: 30 },
    { key: "expectedReturnNew", label: "Expected return on new investment", type: "percent", default: 12, min: 1, max: 30, assumptionKey: "equityReturn" },
  ],
  chart: {
    type: "bar",
    xKey: "name",
    series: [{ key: "amount", label: "Amount", colorVar: "--gold" }],
  },
  compute(values) {
    const plan = retirementPlan({
      currentAge: Number(values.currentAge) || 0,
      retirementAge: Number(values.retirementAge) || 0,
      lifeExpectancy: Number(values.lifeExpectancy) || 0,
      monthlyExpense: Number(values.monthlyExpense) || 0,
      currentSavings: Number(values.currentSavings) || 0,
      inflation: (Number(values.inflation) || 0) / 100,
      postReturn: (Number(values.postReturn) || 0) / 100,
      returnOnExisting: (Number(values.returnOnExisting) || 0) / 100,
      expectedReturnNew: (Number(values.expectedReturnNew) || 0) / 100,
    });

    return {
      headline: { label: "Required monthly SIP", value: plan.monthlySIP },
      extraHeadlines: [
        { label: "Required retirement corpus", value: plan.corpusNeeded },
        { label: "Monthly expense at retirement", value: plan.expenseAtRetirement },
      ],
      breakdown: [
        { label: "Years to retirement", value: plan.yearsToRetire, format: "text" },
        { label: "Years in retirement", value: plan.retirementYears, format: "text" },
        { label: "Corpus needed", value: plan.corpusNeeded },
        { label: "Current savings grow to", value: plan.provisionFV },
        { label: "Shortfall to build", value: plan.shortfall },
      ],
      series: [
        { name: "Corpus needed", amount: plan.corpusNeeded },
        { name: "From current savings", amount: plan.provisionFV },
        { name: "Shortfall (SIP)", amount: plan.shortfall },
      ],
    };
  },
};

export default retirementPlanningConfig;
